import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toDateInTimezone } from 'app/timezone';
import DateTimeInput from 'components/Inputs/DateTimeInput';
import { useFlashTransition } from 'hooks/useFlashState';
import { TimezoneWarning } from 'ts-components/TimezoneWarning';
import { FIELD_TYPES } from 'utility/constants';
import { BROADCAST_FORMDATA } from './constants';
import { isDateInFuture, isDateWithinNext24Hours } from './helpers';
import { DateSection } from './styled';

export const BroadcastDate = ({
  date: dateProp,
  setFormData,
  isValidDate,
  isTodaySmartDate,
  setIsTodaySmartDate,
  setIsValidDate,
  businessTimezone,
  smartSendTimeSplitPercentage,
  disabled,
}) => {
  const { t } = useTranslation();
  const [message, showMessage, flash] = useFlashTransition();

  const [date, setDate] = useState(() =>
    dateProp
      ? new Date(
          dateProp.toLocaleString('en-US', { timeZone: businessTimezone })
        )
      : null
  );

  const localDate = useMemo(() => {
    if (date) {
      return toDateInTimezone(date, businessTimezone);
    }

    return date;
  }, [date, businessTimezone]);

  const onChange = useCallback(
    (d) => {
      const local = toDateInTimezone(d, businessTimezone);

      setFormData(BROADCAST_FORMDATA.date, local);

      if (smartSendTimeSplitPercentage) {
        setFormData(BROADCAST_FORMDATA.rescheduleAt, local);
        setIsTodaySmartDate(isDateWithinNext24Hours(local));
      } else {
        setIsTodaySmartDate(false);
      }

      setIsValidDate(isDateInFuture(local));
      setDate(d);
    },
    [
      setFormData,
      businessTimezone,
      smartSendTimeSplitPercentage,
      setIsValidDate,
      setIsTodaySmartDate,
    ]
  );

  const dateError = useMemo(() => {
    if (disabled) return null;
    return isValidDate
      ? isTodaySmartDate
        ? t('Smart sending must be enabled at least one day in advance')
        : null
      : t('Events cannot be scheduled in the past');
  }, [isValidDate, t, isTodaySmartDate, disabled]);

  useEffect(() => {
    if (date && dateError) {
      flash(dateError);
    }
  }, [date, dateError, flash]);

  return (
    <DateSection>
      <DateTimeInput
        inModal
        isTimeSearchable
        label={t('Scheduled Date and Time')}
        disabled={disabled}
        underline={false}
        value={date}
        onChange={onChange}
        error={Boolean(date && dateError)}
        validate={{ message, showMessage }}
        data-field-id="date"
        data-field-type={FIELD_TYPES.DateTime.type}
      />
      <TimezoneWarning date={localDate} />
    </DateSection>
  );
};
